import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link2, Search, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';

/**
 * Dialog to attach a CRM facture to an EXISTING Chorus invoice.
 * Two paths:
 *  1. Auto-search — calls POST /chorus/factures/{id}/search-chorus which queries
 *     Chorus by our internal numero and returns candidates.
 *  2. Manual entry — paste the CPP ID copied from chorus-pro.gouv.fr.
 *
 * In both cases, on submit we POST /chorus/factures/{id}/link which stores the
 * Chorus ID AND polls the lifecycle status immediately.
 */
export default function LinkChorusDialog({ open, onOpenChange, facture, onLinked }) {
  const [manualId, setManualId] = useState('');
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [candidates, setCandidates] = useState(null); // null | [] | [...]

  const reset = () => { setManualId(''); setCandidates(null); };
  const close = () => { reset(); onOpenChange(false); };

  const runSearch = async () => {
    setSearching(true); setCandidates(null);
    try {
      const r = await api.post(`/chorus/factures/${facture.facture_id}/search-chorus`);
      setCandidates(r.data.candidates || []);
      if ((r.data.candidates || []).length === 0) {
        toast.info("Aucun résultat trouvé — saisissez l'ID Chorus manuellement.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La recherche Chorus a échoué", { duration: 7000 });
    }
    setSearching(false);
  };

  const doLink = async (chorus_id) => {
    if (!chorus_id || !String(chorus_id).trim()) {
      toast.error("ID Chorus requis");
      return;
    }
    setLinking(true);
    try {
      const r = await api.post(`/chorus/factures/${facture.facture_id}/link`, {
        chorus_id: String(chorus_id).trim(),
      });
      toast.success(`Facture liée à Chorus n° ${r.data.facture.chorus_id} — statut ${r.data.facture.chorus_status}`);
      onLinked?.(r.data.facture);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lier la facture", { duration: 7000 });
    }
    setLinking(false);
  };

  if (!facture) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-xl" data-testid="link-chorus-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-purple-600" />
            Lier à une facture Chorus existante
          </DialogTitle>
          <DialogDescription>
            Votre facture <b>{facture.numero}</b> a déjà été déposée sur chorus-pro.gouv.fr ?
            Récupérez son statut dans le CRM en la rattachant à son ID Chorus.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Auto-search */}
          <div className="bg-purple-50/60 border border-purple-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-purple-900 flex items-center gap-1.5">
              <Search className="w-4 h-4" /> Rechercher automatiquement dans Chorus
            </p>
            <p className="text-xs text-purple-700/80 mt-1">
              Le CRM va interroger Chorus avec le n° <b>{facture.numero}</b>.
            </p>
            <Button
              onClick={runSearch}
              disabled={searching || linking}
              className="mt-3 bg-purple-600 hover:bg-purple-700"
              data-testid="link-chorus-search-btn"
            >
              {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {searching ? 'Recherche…' : 'Lancer la recherche'}
            </Button>

            {candidates !== null && candidates.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="link-chorus-candidates">
                {candidates.map((c, i) => (
                  <li
                    key={i}
                    className="bg-white rounded-lg border border-purple-200 p-3 flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">
                        n° Chorus {c.chorus_id} · {c.statut || '—'}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {c.numero_facture || '—'}
                        {c.destinataire && <> · {c.destinataire}</>}
                        {c.montant_ttc && <> · {c.montant_ttc} €</>}
                        {c.date_depot && <> · {String(c.date_depot).slice(0, 10)}</>}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => doLink(c.chorus_id)}
                      disabled={linking}
                      data-testid={`link-chorus-candidate-${c.chorus_id}`}
                    >
                      {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                      Lier
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {candidates !== null && candidates.length === 0 && (
              <p className="text-xs text-slate-500 italic mt-3">
                Aucun résultat — Chorus ne trouve pas cette facture sous ce numéro.
                Utilisez la saisie manuelle ci-dessous.
              </p>
            )}
          </div>

          {/* Manual entry */}
          <div className="border border-slate-200 rounded-xl p-4">
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              <Link2 className="w-4 h-4 text-slate-500" />
              ou saisir l'ID Chorus manuellement
            </Label>
            <p className="text-xs text-slate-500 mt-1">
              Trouvez l'<b>« Identifiant CPP »</b> sur la page de votre facture dans
              <a
                href="https://chorus-pro.gouv.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline inline-flex items-center gap-0.5 ml-1"
              >
                chorus-pro.gouv.fr <ExternalLink className="w-3 h-3" />
              </a>
              {' '}(format numérique, ex : 1234567).
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Input
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                placeholder="Ex : 1234567"
                data-testid="link-chorus-manual-input"
                disabled={linking}
              />
              <Button
                onClick={() => doLink(manualId)}
                disabled={linking || !manualId.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="link-chorus-manual-btn"
              >
                {linking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                Lier
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end pt-2">
          <Button variant="outline" onClick={close} disabled={linking}>Annuler</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
